import { Button } from '@mui/material';
import { AxiosError } from 'axios';
import { Form, Formik, FormikHelpers } from 'formik';
import { map } from 'lodash';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MutationOptions } from 'react-query';
import * as Yup from 'yup';

import { merchantsApi } from 'api';
import {
  DataWrapper,
  FormActions,
  FormControls,
  FormikCheckbox,
  FormikMultiSelect,
  FormikNumericField,
  FormikTextField,
} from 'components';
import { QueryKey } from 'enums';
import { useCurrencies, useMutation, usePartialQuery, useUser } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { Merchant, MerchantCustomBalanceInfo, User } from 'types';
import { validationUtils } from 'utils';

type Props = {
  user: User;
};

type UserMerchantSettingsFormValues = {
  depositAmount: number;
  hiddenAssetCurrencies: string[];
  customBalanceInfo: MerchantCustomBalanceInfo;
};

const CUSTOM_BALANCE_INFO_HEADER_MAX_LENGTH = 100;
const CUSTOM_BALANCE_INFO_CONTENT_MAX_LENGTH = 300;

export const UserMerchantSettingsForm: React.FC<Props> = ({ user }) => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.user_details.merchant_user',
  });
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);

  const { role } = useUser();

  const defaultCustomBalanceInfo = useMemo<MerchantCustomBalanceInfo>(
    () => ({ enabled: false, header: '', content: '' }),
    [],
  );

  const { assetCurrencies, defaultAssetCurrency, getAssetCurrencySymbol } =
    useCurrencies();
  const hiddenAssetCurrencyOptions = useMemo(
    () =>
      map(assetCurrencies, ({ code }) => ({
        value: code,
        label: code,
      })),
    [assetCurrencies],
  );

  const schema = useMemo(
    () =>
      Yup.object().shape({
        depositAmount: Yup.number().min(
          0,
          tCommon('errors.natural_or_zero_number'),
        ),
        hiddenAssetCurrencies: Yup.array().of(Yup.string()),
        customBalanceInfo: Yup.object().shape({
          enabled: Yup.boolean().required(),
          header: Yup.string()
            .max(
              CUSTOM_BALANCE_INFO_HEADER_MAX_LENGTH,
              `${tCommon(
                'errors.max',
              )} ${CUSTOM_BALANCE_INFO_HEADER_MAX_LENGTH}`,
            )
            .when('enabled', {
              is: true,
              then: (schema) => schema.required(tCommon('errors.required')),
            }),
          content: Yup.string()
            .max(
              CUSTOM_BALANCE_INFO_CONTENT_MAX_LENGTH,
              `${tCommon(
                'errors.max',
              )} ${CUSTOM_BALANCE_INFO_CONTENT_MAX_LENGTH}`,
            )
            .when('enabled', {
              is: true,
              then: (schema) => schema.required(tCommon('errors.required')),
            }),
        }),
      }),
    [tCommon],
  );

  const [initialValues, setInitialValues] =
    useState<UserMerchantSettingsFormValues>({
      depositAmount: 0,
      hiddenAssetCurrencies: [],
      customBalanceInfo: { ...defaultCustomBalanceInfo },
    });

  const queryResultMerchant = usePartialQuery(
    [QueryKey.Merchants, user.id],
    () => merchantsApi.getOneByUserId({ userId: user.id }),
    {
      onSuccess: (data) => {
        setInitialValues({
          depositAmount: data.depositAmount || 0,
          hiddenAssetCurrencies: data.hiddenAssetCurrencies || [],
          customBalanceInfo: data.customBalanceInfo ?? {
            ...defaultCustomBalanceInfo,
          },
        });
      },
    },
  );

  const { mutate: updateUserMerchant } = useMutation<
    Merchant,
    AxiosError,
    { id: string; data: UserMerchantSettingsFormValues },
    unknown
  >(merchantsApi.updateAsRole(role));

  const handleUpdateMerchant = useCallback(
    (
      data: UserMerchantSettingsFormValues,
      options:
        | MutationOptions<Merchant, AxiosError, Partial<Merchant>, unknown>
        | undefined,
    ) => {
      updateUserMerchant(
        { id: queryResultMerchant.data?.id || '', data },
        options,
      );
    },
    [updateUserMerchant, queryResultMerchant.data?.id],
  );

  const handleSubmit = useCallback(
    (
      values: UserMerchantSettingsFormValues,
      {
        setSubmitting,
        setErrors,
        resetForm,
      }: FormikHelpers<UserMerchantSettingsFormValues>,
    ) => {
      handleUpdateMerchant(values, {
        onSettled: () => {
          setSubmitting(false);
        },
        onError: (error: AxiosError) => {
          setErrors(validationUtils.getFormErrors(error));
        },
        onSuccess: (data: Merchant) => {
          setInitialValues({
            depositAmount: data.depositAmount || 0,
            hiddenAssetCurrencies: data.hiddenAssetCurrencies || [],
            customBalanceInfo: data.customBalanceInfo ?? {
              ...defaultCustomBalanceInfo,
            },
          });
          resetForm();
        },
      });
    },
    [defaultCustomBalanceInfo, handleUpdateMerchant],
  );

  return (
    <Fragment>
      <div className="tw-text-xl tw-mb-8">{t('title')}</div>
      <DataWrapper queryResult={queryResultMerchant}>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={schema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, isValid }) => (
            <Form className="tw-max-w-xs">
              <FormControls>
                <FormikNumericField
                  name="depositAmount"
                  label={t('fields.deposit_amount')}
                  allowNegative={false}
                  defaultValue={0}
                  suffix={getAssetCurrencySymbol(defaultAssetCurrency?.id)}
                  helperText={t('description.deposit_amount')}
                />
                <FormikMultiSelect
                  name="hiddenAssetCurrencies"
                  label={t('fields.hidden_asset_currencies')}
                  options={hiddenAssetCurrencyOptions}
                  helperText={t('description.hidden_asset_currencies')}
                />
                <FormikCheckbox
                  name="customBalanceInfo.enabled"
                  label={t('fields.custom_balance_info.enabled')}
                />
                <FormikTextField
                  name="customBalanceInfo.header"
                  label={t('fields.custom_balance_info.header')}
                  inputProps={{
                    maxLength: CUSTOM_BALANCE_INFO_HEADER_MAX_LENGTH,
                  }}
                />
                <FormikTextField
                  name="customBalanceInfo.content"
                  label={t('fields.custom_balance_info.content')}
                  multiline
                  rows={4}
                  inputProps={{
                    maxLength: CUSTOM_BALANCE_INFO_CONTENT_MAX_LENGTH,
                  }}
                />
              </FormControls>

              <FormActions>
                <Button
                  type="submit"
                  variant="outlined"
                  disabled={isSubmitting || !isValid}
                >
                  {tCommon('buttons.save')}
                </Button>
              </FormActions>
            </Form>
          )}
        </Formik>
      </DataWrapper>
    </Fragment>
  );
};
